import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  Building2,
  ChevronDown,
  Clock,
  FileDown,
  Loader2,
  MapPin,
  Plus,
  SlidersHorizontal,
  User,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FieldOpsFilterCheckboxRow,
  FieldOpsFilterRow,
} from "@/components/field-ops/field-ops-filters";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useTradeCategories } from "@/hooks/use-config";
import { useProjects } from "@/hooks/use-projects";
import {
  type CreatePunchItemInput,
  type PunchItem,
  useBulkAssignPunchItems,
  useBulkClosePunchItems,
  useCreatePunchItem,
  usePunchAssignableCompanies,
  usePunchList,
} from "@/hooks/use-punch-list";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

type GroupBy = "status" | "priority" | "age";

type PriorityValue = "low" | "medium" | "high" | "critical";

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  ready_for_review: "Ready for Review",
  verified: "Verified",
  closed: "Closed",
  rejected: "Rejected",
  void: "Void",
};

const STATUS_ORDER = [
  "open",
  "in_progress",
  "ready_for_review",
  "verified",
  "rejected",
  "closed",
  "void",
];
const PRIORITY_ORDER: PriorityValue[] = ["critical", "high", "medium", "low"];

function daysSince(dateStr: string | null): number {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
}

function ageBucket(item: PunchItem): string {
  const days = daysSince(item.createdDate);
  if (days <= 7) return "0–7 days";
  if (days <= 14) return "8–14 days";
  if (days <= 30) return "15–30 days";
  return "30+ days";
}

const AGE_BUCKET_ORDER = ["0–7 days", "8–14 days", "15–30 days", "30+ days"];

function statusVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "open":
      return "default";
    case "in_progress":
      return "outline";
    case "ready_for_review":
      return "secondary";
    case "verified":
      return "secondary";
    case "closed":
      return "secondary";
    case "rejected":
      return "destructive";
    case "void":
      return "outline";
    default:
      return "outline";
  }
}

function priorityVariant(
  priority: string | null,
): "default" | "secondary" | "destructive" | "outline" {
  switch (priority) {
    case "critical":
      return "destructive";
    case "high":
      return "destructive";
    case "medium":
      return "default";
    case "low":
      return "secondary";
    default:
      return "outline";
  }
}

function formatPriorityLabel(priority: string | null): string {
  if (!priority) return "—";
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

const TERMINAL_PUNCH_STATUSES = new Set(["closed", "void", "verified"]);

function isItemOverdue(item: PunchItem): boolean {
  if (!item.dueDate) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (item.dueDate >= today) return false;
  return !TERMINAL_PUNCH_STATUSES.has(item.status);
}

interface PunchItemCardProps {
  item: PunchItem;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

function PunchItemCard({ item, selectable, selected, onToggleSelect }: PunchItemCardProps) {
  const overdue = isItemOverdue(item);

  return (
    <Card
      className={cn(
        "gap-0 py-0 shadow-sm transition-colors",
        selected ? "ring-2 ring-primary bg-muted/30" : "hover:bg-muted/50",
      )}
    >
      <CardContent className="px-3 py-2 sm:px-4 sm:py-2.5">
        <div className="flex items-start gap-3">
          {selectable && onToggleSelect && (
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(item.id)}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
              aria-label={`Select punch item ${item.punchNumber}`}
            />
          )}
          <Link to="/punch-list/$id" params={{ id: item.id }} className="flex-1 min-w-0 block">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {item.punchNumber !== null && (
                  <span className="text-xs text-muted-foreground font-mono">
                    #{item.punchNumber}
                  </span>
                )}
                <Badge variant={priorityVariant(item.priority)} className="text-xs">
                  {formatPriorityLabel(item.priority)}
                </Badge>
                <Badge variant={statusVariant(item.status)} className="text-xs">
                  {item.status ? (STATUS_LABELS[item.status] ?? item.status) : "—"}
                </Badge>
                {overdue && (
                  <Badge variant="destructive" className="text-xs">
                    Overdue
                  </Badge>
                )}
              </div>
              <p className="text-sm font-medium line-clamp-2">
                {item.description ?? "No description"}
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                {item.projectName && (
                  <span className="flex items-center gap-1 font-medium text-foreground/80">
                    <Building2 className="h-3 w-3 shrink-0" />
                    {item.projectName}
                  </span>
                )}
                {item.trade && <span>{item.trade}</span>}
                {item.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {item.location}
                  </span>
                )}
                {item.assignedCompanyName && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {item.assignedCompanyName}
                  </span>
                )}
                {item.dueDate && (
                  <span
                    className={cn(
                      "flex items-center gap-1",
                      overdue && "text-destructive font-medium",
                    )}
                  >
                    <Clock className="h-3 w-3" />
                    Due {formatDate(item.dueDate)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

interface GroupSectionProps {
  title: string;
  items: PunchItem[];
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}

function GroupSection({
  title,
  items,
  selectable,
  selectedIds,
  onToggleSelect,
}: GroupSectionProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h2>
        <span className="text-xs bg-muted text-muted-foreground rounded-full px-2 py-0.5">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 pl-1">No items</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <PunchItemCard
              key={item.id}
              item={item}
              selectable={selectable}
              selected={selectedIds?.has(item.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type PunchListFilterStateProps = {
  filterProjectId: string;
  setFilterProjectId: (v: string) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  filterPriority: string;
  setFilterPriority: (v: string) => void;
  filterTrade: string;
  setFilterTrade: (v: string) => void;
  filterLocationInput: string;
  setFilterLocationInput: (v: string) => void;
  filterOverdue: boolean;
  setFilterOverdue: (v: boolean) => void;
  groupBy: GroupBy;
  setGroupBy: (v: GroupBy) => void;
  sortedProjects: Array<{ id: string; name: string }>;
  activeTrades: Array<{ id: string; name: string }>;
  projectsLoading: boolean;
};

type PunchListDesktopFiltersProps = PunchListFilterStateProps;

function buildActiveFilterLabels(props: PunchListFilterStateProps): string[] {
  const labels: string[] = [];
  if (props.filterProjectId !== "all") {
    const project = props.sortedProjects.find((p) => p.id === props.filterProjectId);
    labels.push(project?.name ?? "Project");
  }
  if (props.filterStatus !== "all") {
    labels.push(STATUS_LABELS[props.filterStatus] ?? props.filterStatus);
  }
  if (props.filterPriority !== "all") {
    labels.push(formatPriorityLabel(props.filterPriority));
  }
  if (props.filterTrade !== "all") labels.push(props.filterTrade);
  if (props.filterLocationInput.trim())
    labels.push(`Location: ${props.filterLocationInput.trim()}`);
  if (props.filterOverdue) labels.push("Overdue");
  if (props.groupBy !== "status") {
    labels.push(props.groupBy === "priority" ? "Group: Priority" : "Group: Age");
  }
  return labels;
}

/** Collapsible filter card for mobile — collapsed by default so the list stays visible. */
function PunchListMobileFilters(props: PunchListFilterStateProps) {
  const [open, setOpen] = useState(false);
  const activeLabels = useMemo(() => buildActiveFilterLabels(props), [props]);
  const activeCount = activeLabels.length;

  function clearFilters() {
    props.setFilterProjectId("all");
    props.setFilterStatus("all");
    props.setFilterPriority("all");
    props.setFilterTrade("all");
    props.setFilterLocationInput("");
    props.setFilterOverdue(false);
    props.setGroupBy("status");
  }

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-3 min-h-[44px] text-left sm:px-4"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="pl-mobile-filters-panel"
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Filters</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeCount}
              </Badge>
            )}
          </div>
          {!open && activeCount > 0 && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {activeLabels.slice(0, 3).join(" · ")}
              {activeLabels.length > 3 ? ` · +${activeLabels.length - 3}` : ""}
            </p>
          )}
          {!open && activeCount === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Tap to refine the list</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <CardContent
          id="pl-mobile-filters-panel"
          className="space-y-2 border-t px-3 py-2 sm:px-4 sm:py-2.5"
        >
          <FieldOpsFilterRow label="Project" htmlFor="pl-filter-project">
            <Select
              value={props.filterProjectId}
              onValueChange={props.setFilterProjectId}
              disabled={props.projectsLoading}
            >
              <SelectTrigger id="pl-filter-project" className="min-h-[44px] w-full">
                <SelectValue
                  placeholder={props.projectsLoading ? "Loading projects…" : "All projects"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {props.sortedProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldOpsFilterRow>

          <FieldOpsFilterRow label="Status" htmlFor="pl-filter-status">
            <Select value={props.filterStatus} onValueChange={props.setFilterStatus}>
              <SelectTrigger id="pl-filter-status" className="min-h-[44px] w-full">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </FieldOpsFilterRow>

          <FieldOpsFilterRow label="Priority" htmlFor="pl-filter-priority">
            <Select value={props.filterPriority} onValueChange={props.setFilterPriority}>
              <SelectTrigger id="pl-filter-priority" className="min-h-[44px] w-full">
                <SelectValue placeholder="All priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </FieldOpsFilterRow>

          <FieldOpsFilterRow label="Trade" htmlFor="pl-filter-trade">
            <Select value={props.filterTrade} onValueChange={props.setFilterTrade}>
              <SelectTrigger id="pl-filter-trade" className="min-h-[44px] w-full">
                <SelectValue placeholder="All trades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trades</SelectItem>
                {props.activeTrades.map((t) => (
                  <SelectItem key={t.id} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldOpsFilterRow>

          <FieldOpsFilterRow label="Location" htmlFor="pl-filter-location">
            <Input
              id="pl-filter-location"
              className="min-h-[44px]"
              placeholder="Filter by location…"
              value={props.filterLocationInput}
              onChange={(e) => props.setFilterLocationInput(e.target.value)}
            />
          </FieldOpsFilterRow>

          <FieldOpsFilterCheckboxRow
            id="pl-filter-overdue"
            checked={props.filterOverdue}
            onCheckedChange={props.setFilterOverdue}
            labelText="Overdue only"
          />

          <FieldOpsFilterRow label="Group by" htmlFor="pl-filter-group">
            <Select value={props.groupBy} onValueChange={(v) => props.setGroupBy(v as GroupBy)}>
              <SelectTrigger id="pl-filter-group" className="min-h-[44px] w-full">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="priority">By Priority</SelectItem>
                <SelectItem value="age">By Age</SelectItem>
              </SelectContent>
            </Select>
          </FieldOpsFilterRow>

          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full min-h-[44px] text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all filters
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function PunchListDesktopFilters(props: PunchListDesktopFiltersProps) {
  const {
    filterProjectId,
    setFilterProjectId,
    filterStatus,
    setFilterStatus,
    filterPriority,
    setFilterPriority,
    filterTrade,
    setFilterTrade,
    filterLocationInput,
    setFilterLocationInput,
    filterOverdue,
    setFilterOverdue,
    groupBy,
    setGroupBy,
    sortedProjects,
    activeTrades,
    projectsLoading,
  } = props;

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-3">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-project" className="text-xs text-muted-foreground">
              Project
            </Label>
            <Select
              value={filterProjectId}
              onValueChange={setFilterProjectId}
              disabled={projectsLoading}
            >
              <SelectTrigger id="pl-dt-project" className="h-9 w-full">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {sortedProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-status" className="text-xs text-muted-foreground">
              Status
            </Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger id="pl-dt-status" className="h-9 w-full">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-priority" className="text-xs text-muted-foreground">
              Priority
            </Label>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger id="pl-dt-priority" className="h-9 w-full">
                <SelectValue placeholder="All priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-trade" className="text-xs text-muted-foreground">
              Trade
            </Label>
            <Select value={filterTrade} onValueChange={setFilterTrade}>
              <SelectTrigger id="pl-dt-trade" className="h-9 w-full">
                <SelectValue placeholder="All trades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trades</SelectItem>
                {activeTrades.map((t) => (
                  <SelectItem key={t.id} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-location" className="text-xs text-muted-foreground">
              Location
            </Label>
            <Input
              id="pl-dt-location"
              className="h-9"
              placeholder="Filter by location…"
              value={filterLocationInput}
              onChange={(e) => setFilterLocationInput(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="pl-dt-group" className="text-xs text-muted-foreground">
              Group by
            </Label>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
              <SelectTrigger id="pl-dt-group" className="h-9 w-full">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="priority">By Priority</SelectItem>
                <SelectItem value="age">By Age</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9">
              <Checkbox
                id="pl-dt-overdue"
                checked={filterOverdue}
                onCheckedChange={(v) => setFilterOverdue(v === true)}
              />
              <Label htmlFor="pl-dt-overdue" className="text-sm font-normal cursor-pointer">
                Overdue only
              </Label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface PunchListDesktopTableProps {
  sections: { title: string; items: PunchItem[] }[];
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}

function PunchListDesktopTableRow({
  item,
  selectable,
  selected,
  onToggleSelect,
}: {
  item: PunchItem;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const overdue = isItemOverdue(item);

  return (
    <TableRow className={cn("hover:bg-muted/50", selected && "bg-muted/30")}>
      {selectable && onToggleSelect && (
        <TableCell className="w-10 py-2">
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(item.id)}
            aria-label={`Select punch item ${item.punchNumber}`}
          />
        </TableCell>
      )}
      <TableCell className="py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">
        <Link to="/punch-list/$id" params={{ id: item.id }} className="hover:underline">
          #{item.punchNumber}
        </Link>
      </TableCell>
      <TableCell className="py-2 max-w-[240px]">
        <Link
          to="/punch-list/$id"
          params={{ id: item.id }}
          className="line-clamp-2 font-medium hover:underline"
        >
          {item.description ?? "—"}
        </Link>
      </TableCell>
      <TableCell className="py-2 text-muted-foreground max-w-[140px] truncate">
        {item.projectName ?? "—"}
      </TableCell>
      <TableCell className="py-2 text-muted-foreground max-w-[100px] truncate">
        {item.trade ?? "—"}
      </TableCell>
      <TableCell className="py-2 text-muted-foreground max-w-[120px] truncate">
        {item.location ?? "—"}
      </TableCell>
      <TableCell className="py-2 text-muted-foreground max-w-[120px] truncate">
        {item.assignedCompanyName ?? "—"}
      </TableCell>
      <TableCell
        className={cn(
          "py-2 whitespace-nowrap text-muted-foreground",
          overdue && "text-destructive font-medium",
        )}
      >
        {item.dueDate ? formatDate(item.dueDate) : "—"}
      </TableCell>
      <TableCell className="py-2">
        <Badge variant={priorityVariant(item.priority)} className="text-xs">
          {formatPriorityLabel(item.priority)}
        </Badge>
      </TableCell>
      <TableCell className="py-2">
        <div className="flex flex-wrap gap-1">
          <Badge variant={statusVariant(item.status)} className="text-xs">
            {item.status ? (STATUS_LABELS[item.status] ?? item.status) : "—"}
          </Badge>
          {overdue && (
            <Badge variant="destructive" className="text-xs">
              Overdue
            </Badge>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function PunchListDesktopTable({
  sections,
  selectable,
  selectedIds,
  onToggleSelect,
}: PunchListDesktopTableProps) {
  const colCount = selectable ? 10 : 9;

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {selectable && <TableHead className="w-10" />}
            <TableHead className="w-12">#</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Trade</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Assignee</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sections.map((section) => (
            <Fragment key={section.title}>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell
                  colSpan={colCount}
                  className="py-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground"
                >
                  {section.title}
                  <span className="ml-2 font-normal normal-case">({section.items.length})</span>
                </TableCell>
              </TableRow>
              {section.items.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={colCount}
                    className="py-3 text-sm text-muted-foreground italic"
                  >
                    No items
                  </TableCell>
                </TableRow>
              ) : (
                section.items.map((item) => (
                  <PunchListDesktopTableRow
                    key={item.id}
                    item={item}
                    selectable={selectable}
                    selected={selectedIds?.has(item.id)}
                    onToggleSelect={onToggleSelect}
                  />
                ))
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

interface NewPunchItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Punch list project filter: `"all"` or a project id. */
  listFilterProjectId: string;
  projects: Array<{ id: string; name: string }>;
  projectsLoading?: boolean;
}

function NewPunchItemDialog({
  open,
  onOpenChange,
  listFilterProjectId,
  projects,
  projectsLoading,
}: NewPunchItemDialogProps) {
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [priority, setPriority] = useState<PriorityValue>("medium");
  const [dueDate, setDueDate] = useState("");
  const [roomOrArea, setRoomOrArea] = useState("");
  const [assignedCompanyId, setAssignedCompanyId] = useState<string>("");
  /** Used when list filter is "all"; otherwise list filter supplies project id. */
  const [createProjectId, setCreateProjectId] = useState("");

  const create = useCreatePunchItem();

  const filteredToProject = listFilterProjectId !== "all";
  const resolvedProjectId = filteredToProject ? listFilterProjectId : createProjectId;

  const { options: assigneeOptions, isLoading: assigneesLoading } =
    usePunchAssignableCompanies(resolvedProjectId);

  useEffect(() => {
    if (!open) return;
    if (filteredToProject) setCreateProjectId("");
  }, [open, filteredToProject]);

  // Reset assignee when the target project changes (all-projects flow or filter change).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — sync assignee/room to project
  useEffect(() => {
    setAssignedCompanyId("");
    setRoomOrArea("");
  }, [resolvedProjectId]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    if (!resolvedProjectId) {
      toast.error("Select a project for this punch item.");
      return;
    }

    const input: CreatePunchItemInput = {
      projectId: resolvedProjectId,
      description: description.trim(),
      priority,
    };
    if (location.trim()) input.location = location.trim();
    if (roomOrArea.trim()) input.roomOrArea = roomOrArea.trim();
    if (dueDate) input.dueDate = dueDate;
    if (assignedCompanyId) input.assignedCompanyId = assignedCompanyId;

    create.mutate(input, {
      onSuccess: () => {
        setDescription("");
        setLocation("");
        setPriority("medium");
        setDueDate("");
        setRoomOrArea("");
        setAssignedCompanyId("");
        setCreateProjectId("");
        onOpenChange(false);
      },
    });
  }

  const sortedProjects = [...projects].sort((a, b) => a.name.localeCompare(b.name));
  const filterProjectLabel = sortedProjects.find((p) => p.id === listFilterProjectId)?.name;

  const canSubmit =
    Boolean(description.trim()) &&
    Boolean(resolvedProjectId) &&
    !create.isPending &&
    !projectsLoading &&
    !(resolvedProjectId && assigneesLoading);

  const assigneePlaceholder =
    !resolvedProjectId || assigneesLoading ? "Select project first…" : "Optional — assign trade";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" requireExplicitClose>
        <DialogHeader>
          <DialogTitle>New Punch Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {filteredToProject ? (
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Project </span>
              <span className="font-medium">{filterProjectLabel ?? listFilterProjectId}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="pl-project">Project *</Label>
              <Select
                value={createProjectId || undefined}
                onValueChange={setCreateProjectId}
                disabled={projectsLoading || sortedProjects.length === 0}
              >
                <SelectTrigger id="pl-project" className="min-h-[44px]">
                  <SelectValue
                    placeholder={projectsLoading ? "Loading projects…" : "Select project"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortedProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="pl-description">Description *</Label>
            <Textarea
              id="pl-description"
              placeholder="Describe the deficiency or outstanding item"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pl-location">Location</Label>
            <Input
              id="pl-location"
              placeholder="e.g. Unit 204, Stairwell B"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pl-room">Room / area</Label>
            <Input
              id="pl-room"
              placeholder="e.g. Master bath"
              value={roomOrArea}
              onChange={(e) => setRoomOrArea(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pl-priority">Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as PriorityValue)}>
                <SelectTrigger id="pl-priority" className="min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pl-due-date">Due Date</Label>
              <Input
                id="pl-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="min-h-[44px]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pl-sub">Responsible subcontractor</Label>
            <Select
              value={assignedCompanyId || "__none__"}
              onValueChange={(v) => setAssignedCompanyId(v === "__none__" ? "" : v)}
              disabled={!resolvedProjectId || assigneesLoading}
            >
              <SelectTrigger id="pl-sub" className="min-h-[44px]">
                <SelectValue placeholder={assigneePlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {assigneeOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {resolvedProjectId && !assigneesLoading && assigneeOptions.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Add project team or commitments to pick a responsible company.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-[44px]" disabled={!canSubmit}>
              {create.isPending ? "Creating…" : "Create Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface BulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  selectedIds: string[];
  onSuccess: () => void;
}

function BulkAssignDialog({
  open,
  onOpenChange,
  projectId,
  selectedIds,
  onSuccess,
}: BulkAssignDialogProps) {
  const [companyId, setCompanyId] = useState("");
  const bulkAssign = useBulkAssignPunchItems();
  const { options, isLoading } = usePunchAssignableCompanies(projectId);

  useEffect(() => {
    if (!open) setCompanyId("");
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!companyId) return;
    bulkAssign.mutate(
      { ids: selectedIds, assignedCompanyId: companyId },
      {
        onSuccess: (result) => {
          if (result.succeeded.length > 0) {
            onSuccess();
            onOpenChange(false);
          }
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk assign ({selectedIds.length} items)</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-assign-company">Responsible subcontractor</Label>
            <Select
              value={companyId || undefined}
              onValueChange={setCompanyId}
              disabled={isLoading}
            >
              <SelectTrigger id="bulk-assign-company" className="min-h-[44px]">
                <SelectValue placeholder={isLoading ? "Loading…" : "Select company"} />
              </SelectTrigger>
              <SelectContent>
                {options.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!companyId || bulkAssign.isPending}>
              {bulkAssign.isPending ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface BulkCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSuccess: () => void;
}

function BulkCloseDialog({ open, onOpenChange, selectedIds, onSuccess }: BulkCloseDialogProps) {
  const [note, setNote] = useState("");
  const bulkClose = useBulkClosePunchItems();

  useEffect(() => {
    if (!open) setNote("");
  }, [open]);

  function handleConfirm() {
    bulkClose.mutate(
      { ids: selectedIds, note: note.trim() || undefined },
      {
        onSuccess: (result) => {
          if (result.succeeded.length > 0) {
            onSuccess();
            onOpenChange(false);
          }
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Close {selectedIds.length} punch items?</AlertDialogTitle>
          <AlertDialogDescription>
            Selected items will be marked closed. This cannot be undone from the list view.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="bulk-close-note">Note (optional)</Label>
          <Textarea
            id="bulk-close-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Closeout note for the record"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={bulkClose.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={bulkClose.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {bulkClose.isPending ? "Closing…" : "Close items"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

async function downloadPunchListPdf(projectId: string): Promise<void> {
  const res = await fetch(`/api/punchlist/export?projectId=${encodeURIComponent(projectId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Export failed (${res.status})` }));
    throw new Error((err as { error?: string }).error ?? `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `punch-list-${projectId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export function PunchListPage() {
  const [groupBy, setGroupBy] = useState<GroupBy>("status");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterProjectId, setFilterProjectId] = useState<string>("all");
  const [filterTrade, setFilterTrade] = useState<string>("all");
  const [filterLocationInput, setFilterLocationInput] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkCloseOpen, setBulkCloseOpen] = useState(false);

  const { data: session } = useSession();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: tradeCategories = [] } = useTradeCategories();

  const activeTrades = useMemo(
    () => tradeCategories.filter((t) => t.isActive !== false),
    [tradeCategories],
  );

  const canExportPdf = session?.user?.role !== "contractor";
  const canBulkManage = session?.user?.role !== "contractor";

  useEffect(() => {
    const t = setTimeout(() => setFilterLocation(filterLocationInput.trim()), 300);
    return () => clearTimeout(t);
  }, [filterLocationInput]);

  const { data, isLoading, isError, error, refetch } = usePunchList({
    projectId: filterProjectId !== "all" ? filterProjectId : undefined,
    status: filterStatus !== "all" ? filterStatus : undefined,
    priority: filterPriority !== "all" ? filterPriority : undefined,
    trade: filterTrade !== "all" ? filterTrade : undefined,
    location: filterLocation || undefined,
    overdue: filterOverdue || undefined,
  });

  const items = data ?? [];

  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.id)),
    [items, selectedIds],
  );

  const selectedProjectIds = useMemo(
    () => new Set(selectedItems.map((i) => i.projectId)),
    [selectedItems],
  );

  const bulkAssignProjectId =
    selectedProjectIds.size === 1 ? [...selectedProjectIds][0] : undefined;

  const allVisibleSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const someVisibleSelected = items.some((i) => selectedIds.has(i.id)) && !allVisibleSelected;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedIds(new Set(items.map((i) => i.id)));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function handleSelectAllVisible(checked: boolean) {
    if (checked) selectAllVisible();
    else clearSelection();
  }

  const sortedProjects = [...projects].sort((a, b) => a.name.localeCompare(b.name));

  function groupedSections(): { title: string; items: PunchItem[] }[] {
    if (groupBy === "status") {
      return STATUS_ORDER.map((s) => ({
        title: STATUS_LABELS[s] ?? s,
        items: items.filter((i) => i.status === s),
      }));
    }
    if (groupBy === "priority") {
      return PRIORITY_ORDER.map((p) => ({
        title: formatPriorityLabel(p),
        items: items.filter((i) => i.priority === p),
      }));
    }
    return AGE_BUCKET_ORDER.map((bucket) => ({
      title: bucket,
      items: items.filter((i) => ageBucket(i) === bucket),
    }));
  }

  const sections = groupedSections();

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Punch List</h1>
        <div className="flex items-center gap-2">
          {canExportPdf && filterProjectId !== "all" && (
            <Button
              type="button"
              variant="outline"
              className="hidden lg:inline-flex h-9 gap-2"
              disabled={pdfBusy}
              onClick={() => {
                setPdfBusy(true);
                void downloadPunchListPdf(filterProjectId)
                  .catch((e: Error) => {
                    toast.error(e.message ?? "PDF export failed");
                  })
                  .finally(() => setPdfBusy(false));
              }}
            >
              {pdfBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4" />
              )}
              Export PDF
            </Button>
          )}
          <Button className="min-h-[44px] lg:min-h-9 gap-2" onClick={() => setNewDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Item</span>
          </Button>
        </div>
      </div>

      <div className="hidden lg:block">
        <PunchListDesktopFilters
          filterProjectId={filterProjectId}
          setFilterProjectId={setFilterProjectId}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterPriority={filterPriority}
          setFilterPriority={setFilterPriority}
          filterTrade={filterTrade}
          setFilterTrade={setFilterTrade}
          filterLocationInput={filterLocationInput}
          setFilterLocationInput={setFilterLocationInput}
          filterOverdue={filterOverdue}
          setFilterOverdue={setFilterOverdue}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sortedProjects={sortedProjects}
          activeTrades={activeTrades}
          projectsLoading={projectsLoading}
        />
      </div>

      <div className="lg:hidden">
        <PunchListMobileFilters
          filterProjectId={filterProjectId}
          setFilterProjectId={setFilterProjectId}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterPriority={filterPriority}
          setFilterPriority={setFilterPriority}
          filterTrade={filterTrade}
          setFilterTrade={setFilterTrade}
          filterLocationInput={filterLocationInput}
          setFilterLocationInput={setFilterLocationInput}
          filterOverdue={filterOverdue}
          setFilterOverdue={setFilterOverdue}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sortedProjects={sortedProjects}
          activeTrades={activeTrades}
          projectsLoading={projectsLoading}
        />
      </div>

      {canBulkManage && selectedIds.size > 0 && (
        <Card className="sticky top-0 z-10 gap-0 py-0 shadow-md border-primary/20">
          <CardContent className="flex flex-wrap items-center gap-3 px-3 py-2 sm:px-4 lg:py-2.5">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                onCheckedChange={(v) => handleSelectAllVisible(v === true)}
                aria-label="Select all visible items"
              />
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={clearSelection}
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                className="min-h-[44px] lg:min-h-9"
                disabled={!bulkAssignProjectId}
                title={
                  !bulkAssignProjectId ? "Select items from one project to bulk assign" : undefined
                }
                onClick={() => setBulkAssignOpen(true)}
              >
                Bulk assign
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="min-h-[44px] lg:min-h-9"
                onClick={() => setBulkCloseOpen(true)}
              >
                Bulk close
              </Button>
            </div>
            {!bulkAssignProjectId && selectedIds.size > 1 && (
              <p className="text-xs text-muted-foreground w-full">
                Select items from one project to bulk assign.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {canExportPdf && filterProjectId !== "all" && (
        <div className="flex flex-wrap gap-2 lg:hidden">
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px] gap-2"
            disabled={pdfBusy}
            onClick={() => {
              setPdfBusy(true);
              void downloadPunchListPdf(filterProjectId)
                .catch((e: Error) => {
                  toast.error(e.message ?? "PDF export failed");
                })
                .finally(() => setPdfBusy(false));
            }}
          >
            {pdfBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Export PDF
          </Button>
        </div>
      )}

      {isLoading && (
        <>
          <div className="space-y-4 lg:hidden">
            {(["p1", "p2", "p3", "p4"] as const).map((k) => (
              <Skeleton key={k} className="h-24 w-full rounded-lg" />
            ))}
          </div>
          <div className="hidden lg:block">
            <Skeleton className="h-96 w-full rounded-lg" />
          </div>
        </>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load punch list"}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && items.length === 0 && (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">No punch items found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create the first item to get started.
              </p>
            </div>
            <Button className="min-h-[44px] gap-2" onClick={() => setNewDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              New Item
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <>
          <div className="space-y-6 lg:hidden">
            {sections.map((section) => (
              <GroupSection
                key={section.title}
                title={section.title}
                items={section.items}
                selectable={canBulkManage}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
          <div className="hidden lg:block">
            <PunchListDesktopTable
              sections={sections}
              selectable={canBulkManage}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
          </div>
        </>
      )}

      {canBulkManage && bulkAssignProjectId && (
        <BulkAssignDialog
          open={bulkAssignOpen}
          onOpenChange={setBulkAssignOpen}
          projectId={bulkAssignProjectId}
          selectedIds={[...selectedIds]}
          onSuccess={clearSelection}
        />
      )}

      {canBulkManage && (
        <BulkCloseDialog
          open={bulkCloseOpen}
          onOpenChange={setBulkCloseOpen}
          selectedIds={[...selectedIds]}
          onSuccess={clearSelection}
        />
      )}

      <NewPunchItemDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        listFilterProjectId={filterProjectId}
        projects={projects}
        projectsLoading={projectsLoading}
      />
    </div>
  );
}
